import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Volume2, Loader2, CheckCircle2, Mic2 } from 'lucide-react';
import { toast } from 'sonner';
import useElevenLabsTTS from '@/hooks/useElevenLabsTTS';

/**
 * Grown-up Controls — parent-only knobs for the AI narrator.
 * Persists to localStorage under key `narratorPrefs`:
 *   { voice_id, voice, speed }
 * Any component that uses useElevenLabsTTS reads these prefs on every speak() call.
 */

const VOICES = [
  {
    id: '21m00Tcm4TlvDq8ikWAM',
    label: 'Rachel',
    subtitle: 'American female · warm, mature',
    sample: "Hi there! I'm Rachel. Let's learn something new today.",
    accent: 'us',
    default: true,
  },
  {
    id: 'EXAVITQu4vr4xnSDxMaL',
    label: 'Bella',
    subtitle: 'American female · young, cheerful',
    sample: "Hi friend! I'm Bella. Ready to have some fun?",
    accent: 'us',
  },
  {
    id: 'MF3mGyEYCl7XYWbV9V6O',
    label: 'Elli',
    subtitle: 'American female · young, expressive',
    sample: "Hey! I'm Elli. This is going to be so much fun!",
    accent: 'us',
  },
  {
    id: 'TxGEqnHWrfWFTfGW9XjX',
    label: 'Josh',
    subtitle: 'American male · young, friendly',
    sample: "Hi there. I'm Josh. Let's get started with your lesson.",
    accent: 'us',
  },
  {
    id: 'pFZP5JQG7iQjIQuC4Bku',
    label: 'Lily',
    subtitle: 'British female · classic (previous default)',
    sample: "Hello! I'm Lily. Shall we begin your lesson today?",
    accent: 'uk',
  },
];

const DEFAULT_PREFS = {
  voice_id: (VOICES.find((v) => v.default) || VOICES[0]).id,
  voice: 'nova',
  speed: 0.95,
};

export const readNarratorPrefs = () => {
  try {
    const raw = localStorage.getItem('narratorPrefs');
    if (!raw) return { ...DEFAULT_PREFS };
    const stored = JSON.parse(raw) || {};
    // Migration: users who saved the old British default before we switched to
    // American Rachel would still be stuck on Lily. If their stored voice_id
    // is the old default AND they haven't explicitly re-picked it since (i.e.,
    // narratorPrefs.explicit is not set), silently migrate them to Rachel.
    const OLD_BRITISH = 'pFZP5JQG7iQjIQuC4Bku';
    if (stored.voice_id === OLD_BRITISH && !stored.explicit) {
      const migrated = { ...DEFAULT_PREFS, ...stored, voice_id: DEFAULT_PREFS.voice_id };
      try { localStorage.setItem('narratorPrefs', JSON.stringify(migrated)); } catch { /* ignore */ }
      return migrated;
    }
    return { ...DEFAULT_PREFS, ...stored };
  } catch {
    return { ...DEFAULT_PREFS };
  }
};

const NarratorSettings = () => {
  const [prefs, setPrefs] = useState(readNarratorPrefs());
  const [testingId, setTestingId] = useState(null);
  const { speak, stop, isSpeaking } = useElevenLabsTTS();

  useEffect(() => {
    try {
      localStorage.setItem('narratorPrefs', JSON.stringify(prefs));
    } catch { /* ignore quota errors */ }
  }, [prefs]);

  const selected = useMemo(() => VOICES.find((v) => v.id === prefs.voice_id) || VOICES[0], [prefs.voice_id]);

  const testVoice = async (v) => {
    setTestingId(v.id);
    try {
      await speak(v.sample, { voiceId: v.id, speed: prefs.speed });
    } finally {
      setTimeout(() => setTestingId(null), 4000);
    }
  };

  const resetDefaults = () => {
    stop();
    setPrefs({ ...DEFAULT_PREFS });
    toast.success('Narrator reset to default (Rachel · 0.95x)');
  };

  return (
    <Card data-testid="narrator-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic2 className="w-5 h-5 text-purple-500" />
          Grown-up Controls · Narrator Voice
        </CardTitle>
        <CardDescription>
          Pick the voice and pace the AI reads out loud in — for stories, quizzes, and
          Little Learner mode. Only grown-ups see this section.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3" data-testid="narrator-voices-grid">
          {VOICES.map((v) => {
            const active = prefs.voice_id === v.id;
            return (
              <div
                key={v.id}
                className={`rounded-xl border-2 p-3 transition-all ${
                  active ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    onClick={() => setPrefs((p) => ({ ...p, voice_id: v.id, explicit: true }))}
                    className="flex-1 text-left"
                    data-testid={`narrator-voice-${v.label.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-800">{v.label}</p>
                      {v.accent === 'us' && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                          US
                        </span>
                      )}
                      {v.accent === 'uk' && (
                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold">
                          UK
                        </span>
                      )}
                      {active && <CheckCircle2 className="w-4 h-4 text-purple-500" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{v.subtitle}</p>
                  </button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testVoice(v)}
                    disabled={isSpeaking && testingId === v.id}
                    data-testid={`narrator-test-${v.label.toLowerCase()}`}
                  >
                    {isSpeaking && testingId === v.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Volume2 className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800">Speaking speed</p>
              <p className="text-xs text-slate-500">
                {prefs.speed.toFixed(2)}× — {prefs.speed < 0.9 ? 'slower and clearer' : prefs.speed > 1.0 ? 'faster' : 'natural'}
              </p>
            </div>
            <span className="text-2xl font-extrabold text-purple-600 tabular-nums">
              {prefs.speed.toFixed(2)}×
            </span>
          </div>
          <Slider
            value={[prefs.speed]}
            onValueChange={(v) => setPrefs((p) => ({ ...p, speed: Number(v[0].toFixed(2)) }))}
            min={0.85}
            max={1.1}
            step={0.05}
            aria-label="Narrator speaking speed"
            data-testid="narrator-speed-slider"
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetDefaults}
            className="text-slate-500"
            data-testid="narrator-reset"
          >
            Reset to defaults
          </Button>
          <Button
            onClick={() => testVoice(selected)}
            disabled={isSpeaking}
            className="bg-purple-500 hover:bg-purple-600"
            data-testid="narrator-preview"
          >
            {isSpeaking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Volume2 className="w-4 h-4 mr-1" />}
            Preview {selected.label}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NarratorSettings;
